import type { MeshData } from "@cfg/kernel";
import type { BuildStats, DesignDescription, ValidationIssue } from "@cfg/kernel/worker";
import { createStore } from "zustand/vanilla";

export type ConfiguratorStatus = "idle" | "loading" | "building" | "ready" | "error";

export interface ConfiguratorState {
  key: string | null;
  description: DesignDescription | null;
  params: Record<string, unknown>;
  /** Params the design was opened with (saved config or defaults); used for `dirty`. */
  baseline: Record<string, unknown>;
  mesh: MeshData | null;
  stats: BuildStats | null;
  issues: ValidationIssue[];
  status: ConfiguratorStatus;
  error: string | null;
  buildCount: number;
  /** Which param the user last touched, for the viewport to highlight its label. */
  activeParam: string | null;

  setParams: (patch: Record<string, unknown>) => void;
  replaceParams: (params: Record<string, unknown>, asBaseline?: boolean) => void;
  reset: () => void;
  applyPreset: (id: string) => void;
  setActiveParam: (name: string | null) => void;
  markSaved: () => void;
}

export type ConfiguratorStore = ReturnType<typeof createConfiguratorStore>;

export function createConfiguratorStore() {
  return createStore<ConfiguratorState>((set, get) => ({
    key: null,
    description: null,
    params: {},
    baseline: {},
    mesh: null,
    stats: null,
    issues: [],
    status: "idle",
    error: null,
    buildCount: 0,
    activeParam: null,

    setParams: (patch) => set((s) => ({ params: { ...s.params, ...patch } })),
    replaceParams: (params, asBaseline = false) => set(asBaseline ? { params, baseline: params } : { params }),
    reset: () => {
      const d = get().description;
      if (d) set({ params: { ...d.defaults } });
    },
    applyPreset: (id) => {
      const d = get().description;
      const presets = (d?.ui?.presets ?? []) as { id: string; params: Record<string, unknown> }[];
      const preset = presets.find((p) => p.id === id);
      if (d && preset) set({ params: { ...d.defaults, ...preset.params } });
    },
    setActiveParam: (name) => set({ activeParam: name }),
    markSaved: () => set((s) => ({ baseline: s.params })),
  }));
}

export function isDirty(s: Pick<ConfiguratorState, "params" | "baseline">): boolean {
  const keys = new Set([...Object.keys(s.params), ...Object.keys(s.baseline)]);
  for (const k of keys) if (s.params[k] !== s.baseline[k]) return true;
  return false;
}
